if (!customElements.get('custom-scalapay')) {
  customElements.define(
    'custom-scalapay',
    class CustomScalapay extends HTMLElement {
      constructor() {
        super();
        this.currentPriceCents = parseInt(this.dataset.initialPrice, 10) || 0;
        this.onBuyButtonPricesChanged = this.onBuyButtonPricesChanged.bind(this);
        this.onProductPriceChanged = this.onProductPriceChanged.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
      }

      connectedCallback() {
        this.amountEl = this.querySelector('.custom-scalapay-amount');
        this.render();
        document.addEventListener('buy-button-prices:changed', this.onBuyButtonPricesChanged);
        document.addEventListener('product-price:changed', this.onProductPriceChanged);
        document.addEventListener('variant:change', this.onVariantChange);
        this.setupScalapayOpener();
      }

      disconnectedCallback() {
        document.removeEventListener('buy-button-prices:changed', this.onBuyButtonPricesChanged);
        document.removeEventListener('product-price:changed', this.onProductPriceChanged);
        document.removeEventListener('variant:change', this.onVariantChange);
      }

      onBuyButtonPricesChanged(event) {
        const rawPrice = parseInt(event.detail.rawPrice, 10);
        if (isNaN(rawPrice)) return;

        if (document.querySelector('toggle-cross-sell') && this.hasActiveCrossSell()) return;

        this.currentPriceCents = rawPrice;
        this.render();
      }

      onProductPriceChanged(event) {
        if (!event.detail?.price) return;
        this.currentPriceCents = this.parseFormattedPriceToCents(event.detail.price);
        this.render();
      }

      onVariantChange(event) {
        if (document.querySelector('subscribe-widget') || document.querySelector('deployer-quantity-breaks')) return;

        const variant = event.detail?.variant;
        if (!variant) return;

        let cents = variant.price;
        if (this.hasActiveCrossSell()) {
          cents = this.getCrossSellTotalCents(cents);
        }

        this.currentPriceCents = cents;
        this.render();
      }

      hasActiveCrossSell() {
        const toggleCrossSell = document.querySelector('toggle-cross-sell');
        if (!toggleCrossSell) return false;
        return toggleCrossSell.querySelector('.deployer-checkbox input:checked') !== null;
      }

      getCrossSellTotalCents(baseCents) {
        const toggleCrossSell = document.querySelector('toggle-cross-sell');
        if (!toggleCrossSell) return baseCents;

        let total = baseCents;
        toggleCrossSell.querySelectorAll('.toggle-cross-sell__product').forEach((product) => {
          const checkbox = product.querySelector('.deployer-checkbox input');
          if (checkbox?.checked) {
            total += parseInt(checkbox.dataset.variantPrice, 10) || 0;
          }
        });
        return total;
      }

      parseFormattedPriceToCents(formatted) {
        const cleaned = formatted.replace(/[^\d,.-]/g, '').replace(',', '.');
        const value = parseFloat(cleaned);
        return isNaN(value) ? this.currentPriceCents : Math.round(value * 100);
      }

      render() {
        if (!this.amountEl || this.currentPriceCents <= 0) return;

        const installmentCents = Math.round(this.currentPriceCents / 3);
        this.amountEl.textContent =
          typeof formatPrice === 'function' ? formatPrice(installmentCents) : this.fallbackFormat(installmentCents);
      }

      fallbackFormat(cents) {
        return (cents / 100).toFixed(2).replace('.', ',') + '€';
      }

      setupScalapayOpener() {
        const opener = this.querySelector('.custom-scalapay-logos');
        if (!opener) return;

        opener.addEventListener('click', () => this.openScalapayModal());
      }

      async openScalapayModal() {
        const link = await this.findScalapayInstallmentLink();
        link?.click();
      }

      async findScalapayInstallmentLink() {
        const widget = await this.waitForElement('scalapay-widget');
        await customElements.whenDefined('scalapay-widget');

        const widgetShadow = await this.waitForShadowRoot(widget);
        const installments = await this.waitForElementInRoot(widgetShadow, 'scalapay-pay-installments');
        const installmentsShadow = await this.waitForShadowRoot(installments);
        return installmentsShadow.querySelector('.sp-pay-installment__link');
      }

      waitForElement(selector, root = document, timeout = 30000) {
        return new Promise((resolve, reject) => {
          const existing = root.querySelector(selector);
          if (existing) {
            resolve(existing);
            return;
          }

          const observerRoot = root === document ? document.body : root;
          const observer = new MutationObserver(() => {
            const el = root.querySelector(selector);
            if (el) {
              observer.disconnect();
              resolve(el);
            }
          });
          observer.observe(observerRoot, { subtree: true, childList: true });

          setTimeout(() => {
            observer.disconnect();
            reject(new Error(`Element "${selector}" not found`));
          }, timeout);
        });
      }

      waitForElementInRoot(root, selector, timeout = 30000) {
        return this.waitForElement(selector, root, timeout);
      }

      waitForShadowRoot(host, timeout = 30000) {
        return new Promise((resolve, reject) => {
          if (host.shadowRoot) {
            resolve(host.shadowRoot);
            return;
          }

          const startedAt = Date.now();
          const poll = () => {
            if (host.shadowRoot) {
              resolve(host.shadowRoot);
              return;
            }
            if (Date.now() - startedAt > timeout) {
              reject(new Error('Shadow root not found'));
              return;
            }
            requestAnimationFrame(poll);
          };
          poll();
        });
      }
    }
  );
}
